// Dev-toolkit override store — persists feature-flag overrides across
// reloads via localStorage. Only consumed in dev mode; the analytics
// `checkGate()` short-circuits to the override when one is set, so
// engineers can flip flags locally without touching the Statsig dashboard.
//
// Override semantics:
//   - undefined  → no override; gate falls through to Statsig
//   - true       → force on
//   - false      → force off

'use client';

import { useSyncExternalStore } from 'react';
import type { StoreApi, UseBoundStore } from 'zustand';
import { persist } from 'zustand/middleware';
import { featureGates } from '@/config/feature-gates';
import { publicHostFlags } from '@/lib/config/network-env';
import { createTrackedStore } from '@/lib/dev/zustand-registry';
export type DevToolkitMode = 'sheet' | 'floating';
export type DevToolkitTab = 'flags' | 'env' | 'auth' | 'seo' | 'realtime' | 'events' | 'helius' | 'api' | 'zustand' | 'impersonate' | 'sdk' | 'confetti' | 'conviction' | 'lifecycle' | 'loading' | 'playgrounds';
export type DevToolkitPanelView = 'list' | 'detail';
export interface DevToolkitPos {
  x: number;
  y: number;
}
export interface DevToolkitSize {
  w: number;
  h: number;
}
export interface DevToolkitState {
  /** Whether the floating FAB is visible (engineer can hide it). */
  visible: boolean;
  /** Whether the panel sheet is open. */
  panelOpen: boolean;
  /** Render mode for the panel — sheet (Radix) or floating (draggable). */
  mode: DevToolkitMode;
  /** Last-active tab — persisted so opening the panel returns to where
   *  the engineer left off (e.g. open it on `helius`, close, reopen
   *  → still on `helius`). */
  activeTab: DevToolkitTab;
  /** Panel sub-navigation: `list` shows the section index, `detail`
   *  shows the body of `activeTab`. Persisted so closing/reopening
   *  the panel returns to whatever surface the engineer last saw —
   *  list view OR mid-section. */
  panelView: DevToolkitPanelView;
  /** Persisted floating-window position (top-left, viewport pixels). */
  floatingPos: DevToolkitPos;
  /** Persisted floating-window size. */
  floatingSize: DevToolkitSize;
  /** Map of gateName → override value. `undefined` (key absent) means
   *  no override. */
  overrides: Record<string, boolean>;
  /** True while the overrides map mirrors the live Statsig rollout for a
   *  real (non-QA) user — set by the flags tab's "View as live user"
   *  action. Any manual override edit or reset breaks the mirror and
   *  clears the flag. */
  liveUserView: boolean;
  /** When true, the Loading-preview overlay forces the current route's
   *  `loading.tsx` skeleton to render over the page. Deliberately NOT
   *  persisted — a transient visual check, never a state to wake up
   *  trapped in after a reload. */
  loadingPreview: boolean;
  /** Set or clear an override. Pass `null` to clear. */
  setOverride: (gateName: string, value: boolean | null) => void;
  /** Clear every override. */
  clearOverrides: () => void;
  /** Replace the whole overrides map with the live-user values and mark
   *  the session as viewing-as-live-user. */
  setLiveUserOverrides: (overrides: Record<string, boolean>) => void;
  /** Toggle the Loading-preview overlay for the current route. */
  setLoadingPreview: (loadingPreview: boolean) => void;
  /** Show / hide the FAB. */
  setVisible: (visible: boolean) => void;
  /** Open / close the panel. */
  setPanelOpen: (open: boolean) => void;
  /** Switch render mode. */
  setMode: (mode: DevToolkitMode) => void;
  /** Persist the active tab. */
  setActiveTab: (tab: DevToolkitTab) => void;
  /** Switch panel sub-view (list ↔ detail). */
  setPanelView: (view: DevToolkitPanelView) => void;
  /** Persist floating-window position. */
  setFloatingPos: (pos: DevToolkitPos) => void;
  /** Persist floating-window size. */
  setFloatingSize: (size: DevToolkitSize) => void;
}
interface PersistedShape {
  visible: boolean;
  panelOpen: boolean;
  overrides: Record<string, boolean>;
  liveUserView: boolean;
  mode: DevToolkitMode;
  activeTab: DevToolkitTab;
  panelView: DevToolkitPanelView;
  floatingPos: DevToolkitPos;
  floatingSize: DevToolkitSize;
}
export const useDevToolkitStore: UseBoundStore<StoreApi<DevToolkitState>> = createTrackedStore<DevToolkitState>('useDevToolkitStore')(persist(set => ({
  visible: true,
  panelOpen: false,
  mode: 'sheet',
  activeTab: 'flags',
  panelView: 'list',
  floatingPos: {
    x: 100,
    y: 100
  },
  floatingSize: {
    w: 480,
    h: 600
  },
  overrides: {},
  liveUserView: false,
  loadingPreview: false,
  setOverride: (gateName: string, value: boolean | null): void => {
    set(state => {
      const next = {
        ...state.overrides
      };
      if (value === null) {
        delete next[gateName];
      } else {
        next[gateName] = value;
      }
      // A manual edit means the map no longer mirrors the live rollout.
      return {
        overrides: next,
        liveUserView: false
      };
    });
  },
  clearOverrides: (): void => {
    set({
      overrides: {},
      liveUserView: false
    });
  },
  setLiveUserOverrides: (overrides: Record<string, boolean>): void => {
    set({
      overrides,
      liveUserView: true
    });
  },
  setLoadingPreview: (loadingPreview: boolean): void => {
    set({
      loadingPreview
    });
  },
  setVisible: (visible: boolean): void => {
    set({
      visible
    });
  },
  setPanelOpen: (panelOpen: boolean): void => {
    set({
      panelOpen
    });
  },
  setMode: (mode: DevToolkitMode): void => {
    set({
      mode
    });
  },
  setActiveTab: (activeTab: DevToolkitTab): void => {
    set({
      activeTab
    });
  },
  setPanelView: (panelView: DevToolkitPanelView): void => {
    set({
      panelView
    });
  },
  setFloatingPos: (floatingPos: DevToolkitPos): void => {
    set({
      floatingPos
    });
  },
  setFloatingSize: (floatingSize: DevToolkitSize): void => {
    set({
      floatingSize
    });
  }
}), {
  name: 'morfi-devkit',
  // Persist visibility + panel open state + overrides + mode +
  // active tab + panel view + floating geometry. `panelOpen` is
  // persisted so a page refresh keeps the panel open if it was
  // open — engineers iterating on a feature don't have to reopen
  // it after every reload.
  partialize: (state: DevToolkitState): PersistedShape => ({
    visible: state.visible,
    panelOpen: state.panelOpen,
    overrides: state.overrides,
    liveUserView: state.liveUserView,
    mode: state.mode,
    activeTab: state.activeTab,
    panelView: state.panelView,
    floatingPos: state.floatingPos,
    floatingSize: state.floatingSize
  }),
  onRehydrateStorage: () => (state: DevToolkitState | undefined): void => {
    if (state === undefined) return;
    for (const [localName, gate] of Object.entries(featureGates)) {
      if (localName === gate.name) continue;
      const remoteOverride = state.overrides[gate.name];
      if (remoteOverride === undefined) continue;
      if (state.overrides[localName] === undefined) {
        state.setOverride(localName, remoteOverride);
      }
      state.setOverride(gate.name, null);
    }
  }
}));

// ─── Hydration gate ───────────────────────────────────────────────────
// Zustand v5's `persist` middleware hydrates ASYNCHRONOUSLY — a breaking
// change from v4. That means a component reading the store on first
// mount sees the INITIAL state values, not the persisted ones, and only
// the persisted values arrive a microtask or two later.
//
// For most consumers (a chip count, a label) the resulting one-frame
// flicker is fine. For the DevToolkit it is not:
//   - `panelOpen=false` on first render makes Radix's `<Sheet>` mount
//     in the closed state. The subsequent `panelOpen=true` flip after
//     hydration animates the panel open from the wrong starting state
//     and (in some Radix builds) is dropped entirely because Sheet's
//     internal state machine has already settled.
//   - `mode='sheet'` on first render mounts the Sheet branch of the
//     conditional. When `mode='floating'` arrives post-hydration we
//     remount into the floating window — the Sheet's mount/unmount
//     animations happen needlessly and any in-flight transition is
//     interrupted.
//   - `floatingPos`/`floatingSize` defaults seed the floating window's
//     Motion x/y values. The floating component intentionally seeds
//     ONCE and never re-syncs (drag would fight reactive sync), so
//     hydration arriving after that seed is silently lost — the window
//     pops to the default position regardless of where the engineer
//     last put it.
//
// `useDevToolkitHydrated()` returns `true` only once persist has finished
// hydrating from localStorage. Render-blocking on it (the DevToolkit
// shell returns `null` until then) means every reader downstream sees
// the persisted values from their first render.
type PersistApi<T> = {
  hasHydrated: () => boolean;
  onFinishHydration: (cb: (state: T) => void) => () => void;
};
function getPersistApi(): PersistApi<DevToolkitState> | null {
  // zustand's typings don't surface the persist API on the bound store; it's
  // attached at runtime by the middleware. Cast in a single line so the
  // FT-09 ALLOW-UNKNOWN directive on the preceding line is honoured.
  type StoreWithPersist = {
    persist?: PersistApi<DevToolkitState>;
  };
  // ALLOW-UNKNOWN: persist API is runtime-attached by zustand middleware
  const api = (useDevToolkitStore as unknown as StoreWithPersist).persist;
  return api ?? null;
}
export function useDevToolkitHydrated() {
  return useSyncExternalStore(subscribeToDevToolkitHydration, getDevToolkitHydration, _temp);
}
function _temp() {
  return false;
}
function subscribeToDevToolkitHydration(onChange: () => void): () => void {
  const api = getPersistApi();
  return api?.onFinishHydration(onChange) ?? (() => undefined);
}
function getDevToolkitHydration(): boolean {
  return getPersistApi()?.hasHydrated() ?? true;
}

// ─── Internal-access gate ─────────────────────────────────────────────
// Overrides only take effect when the running session is INTERNAL —
// i.e. local dev mode OR an authenticated `@morfi.markets` user. The
// flag is module-private and set imperatively by `<DevToolkit />` on
// mount; it is NOT persisted, NOT settable from localStorage, and NOT
// exported as a public API.
//
// A user can still call `grantDevToolkitAccess()` from devtools console
// — client-side defenses are never bulletproof. The point is to stop
// CASUAL tampering: a user editing localStorage's `morfi-devkit` key
// from the Storage panel (or via a browser extension) achieves nothing
// because `getDevToolkitOverride` short-circuits on `accessGranted`.
//
// Real flag enforcement still has to happen server-side; client-side
// gates are UI-shaping only, never a security boundary.

// Engineers running `pnpm dev` always have toolkit access. Bootstrapping
// this synchronously (not via `<DevToolkit />`'s mount effect) means
// modules that read overrides during their own mount — notably
// `<EarlyAccessGate>` near the top of the layout tree — see them on the
// first render rather than after a downstream effect fires. In production
// this stays `false` until `<DevToolkit />` mounts and confirms an
// engineer email.
let accessGranted: boolean = process.env.NODE_ENV !== 'production' || publicHostFlags.devToolkitEnabled;
const devToolkitAccessListeners: Set<() => void> = new Set();

/** Called by `<DevToolkit />` on mount when the user passes the
 *  internal-access check (NODE_ENV=development OR @morfi.markets email). */
export function grantDevToolkitAccess(): void {
  setDevToolkitAccess(true);
}

/** Called by `<DevToolkit />` on unmount and on identity changes that
 *  drop internal-access (e.g. logout). */
export function revokeDevToolkitAccess(): void {
  setDevToolkitAccess(false);
}

/** React subscription for consumers whose rendered output depends on
 *  whether DevToolkit overrides are currently authorised. */
export function subscribeDevToolkitAccess(listener: () => void): () => void {
  devToolkitAccessListeners.add(listener);
  return (): void => {
    devToolkitAccessListeners.delete(listener);
  };
}
export function getDevToolkitAccess(): boolean {
  return accessGranted;
}

/** Non-React getter — used by `lib/analytics/index.ts:checkGate` to
 *  consult overrides outside of React render. Returns `undefined` (no
 *  override) when access has not been granted, even if localStorage
 *  contains override entries. Reads the store's current snapshot
 *  synchronously when access IS granted. */
export function getDevToolkitOverride(gateName: string): boolean | undefined {
  if (!accessGranted) return undefined;
  const overrides = useDevToolkitStore.getState().overrides;
  return Object.hasOwn(overrides, gateName) ? overrides[gateName] : undefined;
}
function setDevToolkitAccess(nextAccessGranted: boolean): void {
  if (accessGranted === nextAccessGranted) return;
  accessGranted = nextAccessGranted;
  for (const listener of devToolkitAccessListeners) {
    listener();
  }
}